/**
 * Score BoG 2026 : 4 indicateurs d'avancement (TIF/VPC/cVPC/HazID) par site
 * vis-à-vis des objectifs officiels de l'équipe Lafarge (source : onglet
 * Google Sheet "Suivi TIF Ciment"), + répartition Encadrement / Maîtrise
 * Opérationnelle des taux TIF et VPC. Remplace le score de gamification par
 * points précédemment affiché sur le Cockpit.
 */
import { useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { PAGES } from "@/constants/testIds";

const METRICS = [
  { key: "tif_pct", label: "TIF" },
  { key: "vpc_pct", label: "VPC" },
  { key: "cvpc_pct", label: "cVPC" },
  { key: "hazid_pct", label: "HazID" },
];

function toneFor(pct) {
  if (pct == null) return { bar: "bg-slate-300", text: "text-slate-400" };
  if (pct < 70) return { bar: "bg-red-500", text: "text-red-600" };
  if (pct < 90) return { bar: "bg-amber-500", text: "text-amber-600" };
  return { bar: "bg-emerald-500", text: "text-emerald-600" };
}

function PopulationTable({ title, objective, rows }) {
  return (
    <div data-testid={`bog-score-population-${title.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        <span className="text-[11px] text-slate-400">{objective}</span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const tone = toneFor(r.pct);
          return (
            <div key={r.site} className="grid grid-cols-[130px_1fr_44px] items-center gap-2 text-xs">
              <span className="truncate font-medium text-slate-600">{r.site}</span>
              <Progress value={r.pct ?? 0} className="h-1.5" indicatorClassName={tone.bar} />
              <span className={`text-right font-semibold tabular-nums ${tone.text}`}>{r.pct ?? "-"}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ScoreBogPage() {
  const { filters, refreshKey } = useFilters();
  const [data, setData] = useState(null);

  useEffect(() => {
    dashboardApi.getBogScore2026(filters).then(({ data }) => setData(data));
  }, [filters, refreshKey]);

  if (!data) return <Skeleton className="h-96 rounded-lg" />;

  return (
    <div data-testid={PAGES.scoreBog} className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Score BoG 2026</h1>
        <p className="text-sm text-slate-500">Avancement TIF / VPC / cVPC / HazID par site vis-à-vis des objectifs officiels 2026.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {METRICS.map((m) => {
          const pct = data.company_average[m.key];
          const tone = toneFor(pct);
          return (
            <div key={m.key} data-testid={`bog-score-company-average-${m.key}`} className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{m.label} - Moyenne LP Ciments</p>
              <p className={`mt-1 text-3xl font-bold tabular-nums ${tone.text}`}>{pct}%</p>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Avancement par site</h2>
        <div className="overflow-x-auto">
          <table data-testid="bog-score-sites-table" className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-left text-slate-400">
                <th className="py-2 pr-3 font-medium">Site</th>
                {METRICS.map((m) => (
                  <th key={m.key} className="py-2 pr-3 font-medium">{m.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.sites.map((s) => (
                <tr key={s.site} data-testid={`bog-score-site-row-${s.site}`} className="border-b border-slate-50 last:border-0">
                  <td className="py-2 pr-3 font-medium text-slate-700">{s.site}</td>
                  {METRICS.map((m) => {
                    const tone = toneFor(s[m.key]);
                    return (
                      <td key={m.key} className={`py-2 pr-3 font-semibold tabular-nums ${tone.text}`}>
                        {s[m.key] ?? "-"}%
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">TIF - Encadrement vs Maîtrise Opérationnelle</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <PopulationTable title="Encadrement" objective="Objectif : 15h TIF/mois" rows={data.encadrement.tif} />
          <PopulationTable title="Maîtrise Opérationnelle" objective="Objectif : 30h TIF/mois" rows={data.maitrise_operationnelle.tif} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">VPC - Encadrement vs Maîtrise Opérationnelle</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <PopulationTable title="Encadrement" objective="Objectif : 4 VPC/mois" rows={data.encadrement.vpc} />
          <PopulationTable title="Maîtrise Opérationnelle" objective="Objectif : 2 VPC/mois" rows={data.maitrise_operationnelle.vpc} />
        </div>
      </div>
    </div>
  );
}
